import React, { FunctionComponent, useEffect, useState, useCallback } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import Jitsi from 'components/Jitsi/Jitsi';
import { broadCastStorage } from 'helpers/utils';
import { decodeMeetingLink, encodeMeetingLink } from 'utils/meetingLinkUtils';
import { ref, get } from 'firebase/database';
import { db } from 'utils/firebase-config';

interface IBroadCast {
  roomStatus: Function;
  roomUpdate: boolean;
}

interface RoomData {
  pk: number;
  topic: string;
  host: {
    pk: number;
    name: string;
  };
  participants: any[];
}

const BroadCast: FunctionComponent<IBroadCast> = (props: IBroadCast) => {
  const { roomStatus, roomUpdate } = props;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const history = useHistory();
  const location: any = useLocation();
  const params = useParams<{ encodedData?: string }>();
  const [broadCastItem, setBroadCastItem] = useState<RoomData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Test function to verify meeting link utilities
  const testMeetingLink = () => {
    try {
      const testRoom = {
        pk: 123,
        topic: 'Test Room',
        host: { pk: 456 },
        participants: []
      };
      
      const encoded = encodeMeetingLink(testRoom);
      console.log('Test encode result:', encoded);
      
      const decoded = decodeMeetingLink(encoded);
      console.log('Test decode result:', decoded);
      
      alert('Meeting link test successful! Check console for details.');
    } catch (error: any) {
      console.error('Meeting link test failed:', error);
      alert('Meeting link test failed: ' + error.message);
    }
  };

  /**
   * Handles meeting link flow by decoding URL parameters
   */
  const handleMeetingLinkFlow = useCallback(async (encodedData: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Decode the meeting link data (minimal: id + timestamp)
      const linkData = decodeMeetingLink(encodedData);

      const roomId = linkData.id;

      // Fetch full room from Firebase by id
      const snap = await get(ref(db, `rooms/${roomId}`));
      if (!snap.exists()) {
        throw new Error('Room not found');
      }
      const roomRaw = snap.val();

      // Ensure room is currently in VIDEO status
      if (roomRaw?.status !== 'VIDEO') {
        setError('The video call has been finished.');
        setIsLoading(false);
        setTimeout(() => {
          history.push('/conversations-nearby');
        }, 3000);
        return;
      }

      // Normalize to expected shape
      const room: RoomData = {
        pk: roomRaw.pk ?? roomId,
        topic: roomRaw.topic,
        host: {
          pk: roomRaw.host?.pk,
          name: roomRaw.host?.name
        },
        participants: roomRaw.participants || []
      };

      // Set room data and clean URL
      setBroadCastItem(room);
      history.replace('/call', { selectedBroadCast: room });
      console.log('Meeting link flow completed successfully');
    } catch (error: any) {
      console.error('Meeting link error:', error);
      setError(error.message || 'Unable to join this video');
      
      // For generic errors we can optionally redirect. When error is the finished-message, we have already returned.
    } finally {
      setIsLoading(false);
    }
  }, [history]);

  /**
   * Initializes the component with room data
   */
  useEffect(() => {
    const initializeRoom = () => {
      // Check if room data is in state (existing flow)
      if (location.state?.selectedBroadCast) {
        setBroadCastItem(location.state.selectedBroadCast);
        return;
      }

      // Check if encoded data is in URL params (meeting link flow)
      if (params.encodedData) {
        handleMeetingLinkFlow(params.encodedData);
      } else {
        console.log('No meeting link detected, using existing flow');
        // No meeting link, this might be a direct navigation to /call without data
        // In this case, we should redirect to conversations or show an error
        if (!location.state?.selectedBroadCast) {
          console.log('No room data available, redirecting to conversations');
          history.push('/conversations-nearby');
        }
      }
    };

    initializeRoom();
  }, [location.state, params.encodedData, handleMeetingLinkFlow, history]);

  const handleClose = () => {
    broadCastStorage.clear();
    roomStatus({
      id: broadCastItem?.pk,
      status: 'CHAT',
    });
    if (user.pk !== broadCastItem?.host.pk) {
      history.push('/conversations-nearby');
      window.location.reload();
    }
  };

  useEffect(() => {
    if (location.state?.selectedBroadCast?.host?.pk && 
        user.pk !== location.state.selectedBroadCast.host.pk) {
      if (roomUpdate) {
        history.push('/conversations-nearby');
        window.location.reload();
      }
    }
  }, [roomUpdate, location.state, user.pk, history]);

  useEffect(() => {
    const time = localStorage.getItem('start_time');
    if(time === null || time === '') {
      localStorage.setItem('start_time', JSON.stringify(+new Date()));
    }
  }, []);

  // Loading state for meeting links
  if (isLoading) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '50px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh'
      }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <p className="mt-3">Joining meeting...</p>
      </div>
    );
  }

  // Error state for meeting links
  if (error) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '50px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh'
      }}>
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Unable to Join Meeting</h4>
          <p>{error}</p>
          <hr />
          <p className="mb-0">Redirecting to conversations...</p>
        </div>
      </div>
    );
  }

  // No room data - show error for meeting links
  if (!broadCastItem || !broadCastItem.pk) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '50px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh'
      }}>
        <div className="alert alert-warning" role="alert">
          <h4 className="alert-heading">No Room Data</h4>
          <p>Unable to load room information.</p>
          <p><strong>Debug Info:</strong></p>
          <ul style={{ textAlign: 'left' }}>
            <li>Params: {JSON.stringify(params)}</li>
            <li>Location State: {JSON.stringify(location.state)}</li>
            <li>Current Path: {window.location.pathname}</li>
          </ul>
          <div style={{ marginTop: '20px' }}>
            <button 
              className="btn btn-secondary me-2"
              onClick={testMeetingLink}
            >
              Test Meeting Link
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => history.push('/conversations-nearby')}
            >
              Browse Rooms
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Jitsi handleClose={handleClose} broadCast={broadCastItem} user={user} />
  );
};

export default BroadCast;
